import {
  ListItem,
  Link,
  type ListItemProps,
  Text,
  Flex,
  Box,
  type LinkProps,
} from '@chakra-ui/react';
import NextLink from 'next/link';
import { usePathname } from 'next/navigation';

import { defaultTransition } from 'utils/themeConstants';
import { SVGIcon } from 'types';

type NavItemProps = {
  isActive?: boolean;
  text: string;
  href?: string;
  onClick?: () => void;
  isDisabled?: boolean;
  Icon: SVGIcon;
  iconSize?: string;
} & ListItemProps;

const NavItem = ({
  // active item gets set when the current path matches an item's href
  // but can also be set manually via isActive prop
  isActive,
  text,
  href,
  onClick,
  isDisabled,
  Icon,
  iconSize,
  ...rest
}: NavItemProps) => {
  const height = { base: '2.5rem', md: '3rem' };
  const pathname = usePathname();
  const hrefWithoutParams = href?.split('?')[0];

  const linkProps: LinkProps = {};

  if (!isDisabled && onClick) {
    linkProps.onClick = onClick;
  }

  if (!isDisabled && href) {
    linkProps.as = NextLink;
    linkProps.href = href;
  }

  return (
    <ListItem
      transition={defaultTransition}
      width="100%"
      mb="2px"
      borderRightRadius={'8px'}
      background={(href === pathname || isActive) ? "linear-gradient(90deg, #17181A 0%, #531613 100%)" : "linear-gradient(90deg, #151517 0%, #171719 100%)"}
      position="relative"
      _before={{
        content: '""',
        position: 'absolute',
        inset: 0,
        borderRightRadius: '8px',
        background: (href === pathname || isActive) ? 'linear-gradient(90deg, rgba(255, 0, 0, 0.12) 0%, rgba(255, 0, 0, 0.6) 100%)' : 'linear-gradient(90deg, rgba(255, 255, 255, 0.04) 0%, rgba(255, 255, 255, 0.2) 100%)',
        padding: '2px',
        paddingLeft: 0,
        mask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
        maskComposite: 'exclude',
      }}
      overflow="hidden"
      _hover={{
        background: 'linear-gradient(90deg, #17181A 0%, #751814 100%)',
        _before: {
          background: 'linear-gradient(90deg, rgba(255, 0, 0, 0.16) 0%, rgba(255, 0, 0, 0.8) 100%)',
        }
      }}
      {...rest}
    >
      <Box
        position="absolute"
        right="0"
        width='10px'  
        height='100%'
        background={(href === pathname || isActive) ? 'rgba(216, 56, 50, 1)' : 'rgba(252, 252, 251, 1)'
        }
        transition='width 0.3s ease-in-out'
        filter='blur(10px)'
        opacity={0.2}
      />
      <Link
        variant="unstyled"
        display="flex"
        justifyContent="center"
        alignItems="middle"
        height={height}
        cursor={isDisabled ? 'not-allowed' : 'pointer'}
        _hover={{
          '.icon': {
            opacity: 0,
          },
          '.text': {
            opacity: 1,
          },
        }}
        {...linkProps}
      >
        <Box position="relative" height={height} width="100%">
          <Flex
            className="icon"
            height="full"
            width="full"
            position="absolute"
            top="0"
            left="0"
            opacity={(href === pathname || isActive) ? 0 : 1}
            transition={defaultTransition}
          >
            <Icon
              marginX="auto"
              w={iconSize || { base: '1.25rem', md: '1.5rem' }}
              h="auto"
              opacity={isDisabled ? 0.2 : 1}
            />
          </Flex>

          <Flex
            transition={defaultTransition}
            className="text"
            alignItems="center"
            justifyContent="center"
            opacity={(href === pathname || isActive) ? 1 : 0}
            w="100%"
            h="100%"
          >
            <Text
              fontSize="11px"
              fontWeight="700"
              fontStyle="italic"
              margin="0 auto"
              width="100%"
              background='linear-gradient(90deg, #D83832 0%, #F6685E 100%)'
              backgroundClip='text'
            >
              {text}
            </Text>
          </Flex>
        </Box>
      </Link>
    </ListItem>
  );
};

export default NavItem;
